import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Controller, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { RotateCw } from 'lucide-react';
import { DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import { AutocompleteMultiSelect, type AutocompleteMultiSelectOption, Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../../lib/messages';
import type { TargetingClient, TargetingPublisher } from '../../../../../targeting/api';
import type { LiverampSegment, TargetingPlacement } from '../../../../../targeting/model';
import { CustomTargetingExtField } from '../../../../../targeting/customTargetingExtModel';
import type { MadBuyLineItemGeneralForm } from '../../../lineItemsModel';
import { customTargetingRulePath } from './paths';

const valueSearchMinimumLength = 1;

export function CustomTargetingRuleValueSelect({
  client,
  groupIndex,
  ruleIndex,
}: {
  client: TargetingClient;
  groupIndex: number;
  ruleIndex: number;
}) {
  'use no memo';

  const { control, getFieldState } = useFormContext<MadBuyLineItemGeneralForm>();
  const formState = useFormState({ control });
  const prefix = customTargetingRulePath(groupIndex, ruleIndex);
  const field = useWatch({ control, name: `${prefix}.key` });
  const selectedIds = useWatch({ control, name: `${prefix}.values` });
  const [initial] = useState(() => ({ field, ids: [...selectedIds] }));
  const [selectedOptions, setSelectedOptions] = useState<AutocompleteMultiSelectOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const searchQuery = useQuery({
    queryKey: [...(client.queryScope ?? []), 'custom-targeting-value-search', field, searchTerm],
    enabled: searchTerm.trim().length >= valueSearchMinimumLength,
    queryFn: () => searchValueOptions(client, field, searchTerm),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const resolveEnabled = field === initial.field && initial.ids.length > 0;
  const resolveQuery = useQuery({
    queryKey: [
      ...(client.queryScope ?? []),
      'custom-targeting-value-resolve',
      initial.field,
      initial.ids,
    ],
    enabled: resolveEnabled,
    queryFn: () => resolveValueOptions(client, initial.field, initial.ids),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const options = useMemo(() => {
    const fetched = [
      ...(resolveEnabled ? (resolveQuery.data ?? []) : []),
      ...(searchQuery.data ?? []),
    ];
    return Array.from(
      new Map([...selectedOptions, ...fetched].map((option) => [option.id, option])).values(),
    );
  }, [resolveEnabled, resolveQuery.data, searchQuery.data, selectedOptions]);

  const onSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);
  const loadError = searchQuery.isError || (resolveEnabled && resolveQuery.isError);
  const validationError = getFieldState(`${prefix}.values`, formState).error?.message;

  return (
    <DashboardPageFormField
      label={dashboardT('madBuy.lineItems.customTargetingValue')}
      required
      error={
        loadError ? dashboardT('madBuy.lineItems.customTargetingValuesError') : validationError
      }
    >
      <Controller
        control={control}
        name={`${prefix}.values`}
        render={({ field: valuesField }) => (
          <AutocompleteMultiSelect
            label={dashboardT('madBuy.lineItems.customTargetingValue')}
            value={valuesField.value}
            options={options}
            loading={searchQuery.isLoading || (resolveEnabled && resolveQuery.isLoading)}
            placeholder={searchPlaceholder(field)}
            emptyMessage={dashboardT('madBuy.lineItems.customTargetingNoResults')}
            minSearchLength={valueSearchMinimumLength}
            onSearchTermChange={onSearch}
            searchDebounceMs={300}
            onValueChange={(nextIds) => {
              setSelectedOptions((current) => {
                const byId = new Map([...current, ...options].map((option) => [option.id, option]));
                return nextIds.flatMap((id) => {
                  const option = byId.get(id);
                  return option ? [option] : [];
                });
              });
              valuesField.onChange(nextIds);
            }}
          />
        )}
      />
      {loadError ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-1 w-fit"
          onClick={() => {
            if (searchQuery.isError) void searchQuery.refetch();
            if (resolveQuery.isError) void resolveQuery.refetch();
          }}
        >
          <RotateCw className="h-4 w-4" aria-hidden="true" />
          {dashboardT('targeting.retry')}
        </Button>
      ) : null}
    </DashboardPageFormField>
  );
}

function searchPlaceholder(field: CustomTargetingExtField): string {
  if (field === CustomTargetingExtField.Placement) {
    return dashboardT('madBuy.lineItems.customTargetingSearchPlacements');
  }
  if (field === CustomTargetingExtField.Liveramp) {
    return dashboardT('madBuy.lineItems.customTargetingSearchSegments');
  }
  return dashboardT('madBuy.lineItems.customTargetingSearchPublishers');
}

async function searchValueOptions(
  client: TargetingClient,
  field: CustomTargetingExtField,
  searchTerm: string,
): Promise<AutocompleteMultiSelectOption[]> {
  if (field === CustomTargetingExtField.Placement) {
    return (await client.listPlacements({ searchTerm, size: 20 })).content.map(toPlacementOption);
  }
  if (field === CustomTargetingExtField.Liveramp) {
    return (await client.listLiverampSegments({ searchTerm, size: 20 })).content.map(
      toLiverampOption,
    );
  }
  return (await client.listPublishers({ searchTerm, size: 20 })).content.map(toPublisherOption);
}

async function resolveValueOptions(
  client: TargetingClient,
  field: CustomTargetingExtField,
  ids: string[],
): Promise<AutocompleteMultiSelectOption[]> {
  if (field === CustomTargetingExtField.Placement) {
    return (await client.listPlacements({ secondaryIds: ids })).content.map(toPlacementOption);
  }
  if (field === CustomTargetingExtField.Liveramp) {
    return (await client.listLiverampSegments({ segmentIds: ids })).content.map(toLiverampOption);
  }
  return (await client.listPublishers({ secondaryIds: ids })).content.map(toPublisherOption);
}

function toPublisherOption(publisher: TargetingPublisher): AutocompleteMultiSelectOption {
  return {
    id: publisher.secondaryId ?? publisher.id,
    label: publisher.secondaryId ? `${publisher.name} (${publisher.secondaryId})` : publisher.name,
  };
}

function toPlacementOption(placement: TargetingPlacement): AutocompleteMultiSelectOption {
  return { id: placement.secondaryId ?? placement.id, label: placement.name };
}

function toLiverampOption(segment: LiverampSegment): AutocompleteMultiSelectOption {
  const name = segment.segmentName ?? '';
  const provider = segment.providerName ?? '';
  return {
    id: segment.segmentId ?? segment.id,
    label: provider ? `${name} (${provider})` : name || segment.id,
  };
}
